import Phaser from 'phaser';
import { PlanetScene } from './PlanetScene';

export interface PlanetConfig {
    name: string;
    image: string;
    x: number;
    y: number;
    mass: number;
    size: number;
    velocity: { x: number; y: number };
    color: number;
    tintColor?: number;
    fix?: boolean;
    showOwn?: boolean;
    showForce?: boolean;
    showTail?: boolean;
}

/**
 * 星球
 */
export class Planet {
    G = 20000;
    sprite: Phaser.Physics.Arcade.Sprite;

    // config
    name: string;
    image: string;
    x: number;
    y: number;
    mass: number;
    size: number;
    color: number;
    trail: {x: number, y: number}[] = [];  // 轨迹点数组
    maxTrail: number = 200;
    fix: boolean = false;
    velocity: { x: number; y: number };
    showOwn: boolean = false;
    showForce: boolean = true;
    showTail: boolean = true;

    // control
    selected: boolean = false;

    // ui
    scene: PlanetScene;
    graphics: Phaser.GameObjects.Graphics;
    lblOwn: Phaser.GameObjects.Text;
    lblForces: Phaser.GameObjects.Text[] = [];

    destroy() {
        this.sprite.destroy();
        this.lblOwn.destroy();
        this.lblForces.forEach(text => text.destroy());
    }

    constructor(scene: Phaser.Scene, config: PlanetConfig) {
        // config
        this.name = config.name;
        this.image = config.image;
        this.x = config.x;
        this.y = config.y;
        this.size = config.size;
        this.mass = config.mass;
        this.color = config.color;
        this.fix = config.fix || false;
        this.showOwn = config.showOwn || false;
        this.showForce = config.showForce || false;
        this.showTail = config.showTail || true;
        this.velocity = config.velocity;

        // graphics
        this.scene = scene as PlanetScene;
        this.graphics = scene.add.graphics();

        // sprite
        this.sprite = scene.physics.add.sprite(config.x, config.y, config.image).setDisplaySize(config.size, config.size);
        this.sprite.name = config.name;
        if (config.tintColor) {
            this.sprite.setTint(config.tintColor);
        }
        this.sprite.setVelocity(config.velocity.x, config.velocity.y);
        this.sprite.setInteractive().on('pointerdown', () => {
            this.selected = !this.selected;
        });

        // label
        this.lblOwn = scene.add.text(0, 0, '', { font: '18px Arial', color: '#ff3', backgroundColor: 'rgba(0,0,0,0.4)', padding: { x: 8, y: 4 } });
    }

    //-------------------------------------------------------
    // zoom
    //-------------------------------------------------------
    /** 缩放 */
    setZoom(zoom: number, point: Phaser.Math.Vector2) {
        // spirit 的大小根据 zoom 进行缩放
        // spirit 的位置相对于 point 进行缩放
        this.sprite.setDisplaySize(this.size*zoom, this.size*zoom);
        this.sprite.setPosition(point.x + (this.sprite.x - point.x) * zoom, point.y + (this.sprite.y - point.y) * zoom);
        this.trail.forEach(t => {
            t.x = point.x + (t.x - point.x) * zoom;
            t.y = point.y + (t.y - point.y) * zoom;
        });

        // 调整sprite的当前速度
        var v = this.sprite.body!.velocity;
        this.sprite.setVelocity(v.x * zoom, v.y * zoom);
    }
    
    /** 偏移 */
    offset(dx: number, dy: number) {
        this.sprite.x += dx;
        this.sprite.y += dy;
        this.trail.forEach(t => {
            t.x += dx;
            t.y += dy;
        });
    }

    //-------------------------------------------------------
    // draw
    //-------------------------------------------------------
    /** 绘制边框 */
    drawBorder(graphics: Phaser.GameObjects.Graphics) {
        if (!this.selected) return;
        graphics.lineStyle(4, 0xffffff, 1);
        graphics.strokeCircle(
            this.sprite.x, 
            this.sprite.y, 
            this.sprite.displayWidth/2
        );
    }

    /** 绘制自身信息 */
    drawOwn(graphics: Phaser.GameObjects.Graphics) {
        // border
        if (this.selected)
            this.drawBorder(graphics);

        // own label
        this.lblOwn.setVisible(this.showOwn);
        if (!this.showOwn)  return;

        // speed
        const vx = this.sprite.body!.velocity.x;
        const vy = this.sprite.body!.velocity.y;
        const speed = Math.sqrt(vx * vx + vy * vy);
        const px = this.sprite.x;
        const py = this.sprite.y;
        const realX = this.scene.position.x + this.sprite.x;
        const realY = this.scene.position.y + this.sprite.y;

        // arrow
        const arrowLen = this.size > 50 ? this.size : 50; //80;
        const angle = Math.atan2(vy, vx);
        const endX = px + Math.cos(angle) * arrowLen;
        const endY = py + Math.sin(angle) * arrowLen;
        if (!this.fix)
            this.drawArrow(graphics, px, py, endX, endY, 0xffffff, 4);

        // title
        var tx = this.sprite.x;
        var ty = this.sprite.y + this.sprite.displayHeight/2 + 10;
        this.lblOwn.setPosition(tx, ty).setOrigin(0.5, 0)
            .setText(`${this.sprite.name}\n质量=${this.mass}\n速度=${speed.toFixed(2)}\n坐标=(${realX.toFixed(2)},${realY.toFixed(2)})`)
            .setStyle({ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.4)' })
            ;
    }

    /** 绘制引力箭头和引力信息 */
    drawForce(graphics: Phaser.GameObjects.Graphics, ax: number, ay: number, otherPlanet: Planet, visible: boolean) {
        visible = visible && this.showForce;
        let lblForce = this.lblForces.find(text => text.name === otherPlanet.sprite.name);
        lblForce?.setVisible(visible);
        if (!visible) return;

        // 绘制引力箭头
        const px = this.sprite.x;
        const py = this.sprite.y;
        const accAngle = Math.atan2(ay, ax);
        const accArrowLen = this.size > 50 ? this.size : 50;   // 引力箭头长度= 2倍的自身大小 或者 50
        const otherPlanetColor = otherPlanet.color;
        const accEndX = px + Math.cos(accAngle) * accArrowLen;
        const accEndY = py + Math.sin(accAngle) * accArrowLen;
        this.drawArrow(graphics, px, py, accEndX, accEndY, otherPlanetColor, 4);

        // 计算引力信息
        const dx = otherPlanet.sprite.x - this.sprite.x;
        const dy = otherPlanet.sprite.y - this.sprite.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const force = this.G * this.mass * otherPlanet.mass / (distance * distance);
        const gravityAcc = force / this.mass;
        let accTx = px + Math.cos(accAngle) * (accArrowLen + 20);
        let accTy = py + Math.sin(accAngle) * (accArrowLen + 20);

        // 绘制引力信息
        if (!lblForce) {
            lblForce = graphics.scene.add.text(0, 0, '', { font: '16px Arial', color: Phaser.Display.Color.IntegerToColor(otherPlanetColor).rgba, backgroundColor: 'rgba(0,0,0,0.4)', padding: { x: 6, y: 3 } }).setDepth(5).setAlpha(0.85);
            lblForce.name = otherPlanet.sprite.name;
            this.lblForces.push(lblForce);
            console.log(`${this.sprite.name}: add force ${otherPlanet.sprite.name}`);
        }
        lblForce.setPosition(accTx, accTy)
            .setText(`距离=${distance.toFixed(6)}\n引力=${force.toFixed(6)}\n加速度=${gravityAcc.toFixed(6)}`)
            .setStyle({ color: Phaser.Display.Color.IntegerToColor(otherPlanetColor).rgba, backgroundColor: 'rgba(0,0,0,0.4)' })
            ;
        //console.log(`${this.sprite.name}: show force ${otherPlanet.sprite.name}`);
    }

    /** 绘制轨迹点 */
    drawTrail(graphics: Phaser.GameObjects.Graphics) {
        if (!this.showTail) return;
        this.trail.push({x: this.sprite.x, y: this.sprite.y});
        if (this.trail.length > this.maxTrail) this.trail.shift();
        for (let i = 0; i < this.trail.length; i++) {
            const t = i / this.trail.length;
            const p = this.trail[i];
            const r = 1;
            const alpha = 0.25 + 0.75 * t;
            const color = Phaser.Display.Color.IntegerToColor(this.color).brighten(60 * t).color;
            graphics.fillStyle(color, alpha);
            graphics.fillCircle(p.x, p.y, r);
        }
    }


    /** 绘制箭头 */
    drawArrow(graphics: Phaser.GameObjects.Graphics, x1: number, y1: number, x2: number, y2: number, color: number, lineWidth: number = 4, headLen: number = 14, headAngle: number = Math.PI / 7) {
        graphics.lineStyle(lineWidth, color, 1);
        graphics.beginPath();
        graphics.moveTo(x1, y1);
        graphics.lineTo(x2, y2);
        graphics.strokePath();
        const angle = Math.atan2(y2 - y1, x2 - x1);
        graphics.lineBetween(x2, y2, x2 - Math.cos(angle - headAngle) * headLen, y2 - Math.sin(angle - headAngle) * headLen);
        graphics.lineBetween(x2, y2, x2 - Math.cos(angle + headAngle) * headLen, y2 - Math.sin(angle + headAngle) * headLen);
    }

}
